import React from "react";
import styles from "./DonjonFond.module.css";
import {AMBIANCES, NOMS_OBJETS, OBJETS, REGLAGES} from "./reglages";
import {graine, rectangles} from "../arcade/sprites";
import {resoudre} from "../reglages";

/**
 * Fonds du thème Donjon et aventure : trois ambiances de jeu d'aventure, en CSS et SVG purs (dessins et code originaux, aucun
 * personnage ni objet d'un jeu existant). Un seul composant, trois variantes (`variante`) :
 *   torches : mur de pierre aux flammes qui vacillent ;
 *   brume   : nappes de brume qui dérivent et braises qui montent ;
 *   objets  : coffre, épée, bouclier, potion et clé qui flottent doucement sur le mur.
 * Tout bouge par `transform` ou `opacity` ; les positions sont tirées une fois avec une graine fixe.
 */
const RECTS = NOMS_OBJETS.reduce((acc, nom) => ({...acc, [nom]: rectangles(OBJETS[nom])}), {});

function Objet({nom, couleur}) {
    return (
        <svg viewBox={`0 0 ${OBJETS[nom][0].length} ${OBJETS[nom].length}`} shapeRendering="crispEdges" className={styles.objet} fill={couleur}>
            {RECTS[nom].map((r, i) => <rect key={i} x={r.x} y={r.y} width={r.l} height={1}/>)}
        </svg>
    );
}

function Torches({nombre, ambiance}) {
    const hasard = graine(61);
    return Array.from({length: Math.min(nombre, 8)}, (_, i) => (
        <div key={i} className={styles.torche} style={{left: `${6 + (i + 0.5 * hasard()) * (88 / Math.min(nombre, 8))}%`, top: `${12 + hasard() * 70}%`}}>
            <div className={styles.halo} style={{background: `radial-gradient(circle, ${ambiance.flamme}66, transparent 65%)`, animationDelay: `${-hasard() * 2}s`}}/>
            <div className={styles.flamme} style={{background: `radial-gradient(ellipse at 50% 75%, ${ambiance.coeur} 0 20%, ${ambiance.flamme} 45%, transparent 72%)`, animationDelay: `${-hasard()}s`}}/>
            <div className={styles.manche}/>
        </div>
    ));
}

function Brume({nombre, ambiance}) {
    const hasard = graine(73);
    return (
        <>
            {[0, 1, 2].map(i => (
                <div key={i} className={styles.nappe} style={{bottom: `${i * 18}%`, animationDuration: `${50 + i * 20}s`, animationDelay: `${-i * 15}s`}}/>
            ))}
            {Array.from({length: nombre * 2}, (_, i) => (
                <div key={i} className={styles.braise} style={{left: `${hasard() * 97}%`, background: ambiance.flamme, animationDelay: `${-hasard() * 14}s`, animationDuration: `${9 + hasard() * 8}s`}}/>
            ))}
        </>
    );
}

function Objets({nombre, ambiance}) {
    const hasard = graine(89);
    return Array.from({length: nombre}, (_, i) => (
        <div key={i} className={styles.flotte} style={{left: `${hasard() * 90}%`, top: `${6 + hasard() * 80}%`, animationDelay: `${-hasard() * 6}s`, animationDuration: `${5 + hasard() * 4}s`}}>
            <Objet nom={NOMS_OBJETS[i % NOMS_OBJETS.length]} couleur={ambiance.objets[i % ambiance.objets.length]}/>
        </div>
    ));
}

const VARIANTES = {torches: Torches, brume: Brume, objets: Objets};

export default function DonjonFond({variante = "torches", reglages}) {
    const r = resoudre(REGLAGES, reglages);
    const ambiance = AMBIANCES[r.ambiance] || AMBIANCES.pierre;
    const Variante = VARIANTES[variante] || Torches;
    return (
        <div className={styles.scene} aria-hidden="true"
             style={{"--mur": ambiance.mur, "--mur2": ambiance.mur2, "--joint": ambiance.joint, "--flamme": ambiance.flamme, "--vitesse": 1 / r.vitesse}}>
            <div className={styles.pierres}/>
            <Variante nombre={r.nombre} ambiance={ambiance}/>
            <div className={styles.ombre}/>
        </div>
    );
}

export const fondDonjon = variante => function FondDonjon(props) {
    return <DonjonFond variante={variante} {...props}/>;
};
